import React, {
  useEffect,
  useState,
  useRef,
  useImperativeHandle,
  forwardRef,
} from "react";
import { useTheme } from "@material-ui/core/styles";
import {
  Card,
  CardHeader,
  Avatar,
  Typography,
  TextField,
  Box,
  Button,
  CircularProgress,
  useMediaQuery,
  Modal,
  DialogTitle,
  DialogContent,
  DialogActions,
  Drawer,
  Tooltip,
  ClickAwayListener,
} from "@material-ui/core";
import IconButton from "@material-ui/core/IconButton";
import { Favorite, SentimentSatisfiedAlt, Close } from "@material-ui/icons";
import { InputBase } from "@material-ui/core";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import {
  Bookmark,
  Send,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  DollarSign,
} from "lucide-react";
import { ChevronsLeftRight } from "lucide-react";
import { trackShare } from "../../../../utils/analyticsTracking";
import { Link, useHistory, useLocation } from "react-router-dom";
import Picker from "@emoji-mart/react";
import data from "@emoji-mart/data";
import { getExploreAPI, getMyFeedAPI } from "../../../../api/galleryApi";
import {
  addBookmarkAPI,
  removeBookmarkAPI,
  followUserAPI,
  getFileByIdAPI,
  getUserProfile,
  likeGalleryPostAPI,
  sendCommentToPostAPI,
  getUserGalleryPostCommentsAPI,
  likeUserPostCommentAPI,
  updateGalleryPostAPI,
} from "../../../../api/userApi";
import { showToast } from "../../../../utils/toast";
import { PostDialog } from "../../../elements/modal-post";
import ReportPostDialog from "../../../elements/ReportPostDialog";
import CommentText from "../../../elements/comment-text";
import useInViewPort from "../../../../hooks/interationObserver";
import { checkRateLimit } from "../../../../utils/rateLimit";
import likeAnimStyle from "../../../../styles/likeAnimation";
import UnlockPaidContentModal from "../../../elements/UnlockPaidContentModal";
import TipDialog from "../../../elements/TipDialog";
import VerifiedShieldHelpIcon from "../../../../assets/VerifiedShieldHelpIcon";
import { useAppSelector } from "../../../../redux/typedHooks";
import { useGlobalMonetization } from "../../../../hooks/useGlobalMonetization";
import { UserInterface } from "../../../../types/user";
import { formatPostDate } from "../../../../utils/timeFormatter";
import HeartPostIcon from "../../../../assets/HeartPostIcon";
import CommentPostIcon from "../../../../assets/CommentPostIcon";
import TipPostIcon from "../../../../assets/TipPostIcon";
import SharePostIcon from "../../../../assets/SharePostIcon";
import videoIconSvg from "../../../../assets/videoIcon.svg";
import ShareLinkDialog from "../../../elements/ShareLinkDialog";
import subscribeIcon from "../../../../assets/images/icons/subscribeIcon.svg";
import EllipsisMenuButton from "../../../elements/EllipsisMenuButtons/EllipsisMenuButtonPost";
import CommentsModal, { Comment } from "../../../elements/CommentsModal";
import { useCreatorPostStyles } from "./style";
import { useProfileStyles } from "../../../Header/ProfileViewer/profile-viewer.styles";
import type { Post as ProfileViewerPost } from "../../../Header/ProfileViewer/profile-viewer";
import PostImage from "../../../../assets/images/postImage.png";

enum FollowStatus {
  NOT_FOLLOWING = "not_following",
  FOLLOWING = "following",
  PENDING = "pending",
}

export interface SubscriptionTier {
  id: string;
  tierName: string;
  price: number;
  durationInDays: 30 | 90 | 180;
}

export interface ProfileData {
  _id: string;
  firstName: string;
  lastName: string;
  profileImg: string;
  validated: boolean;
  postsCount: number;
  followersCount: number;
  description: string;
  website: string;
  private: boolean;
  subscriptionPrice: SubscriptionTier[];
  follow: {
    followUserId: string;
    accepted: boolean;
  } | null;
  link?: string;
  suggestedTips?: number[];
  minimumTipAmount?: number;
  customContentPrice?: number;
}

interface Post {
  _id: string;
  userId: string;
  description: string;
  hashtags?: string[];
  categories?: Array<{
    _id: string;
    name: string;
    description: string;
  }>;
  purchasesCount: number;
  viewsCount: number;
  likesCount: number;
  sharesCount?: number;
  isLiked: boolean;
  isBookmarked?: boolean;
  access: boolean;
  price: number;
  type: string;
  subscriptionType?: "basic" | "premium" | null;
  isSubscribed?: boolean;
  hasCreatorSubscription?: boolean;
  creatorSubscriptionTier?: "basic" | "premium" | null;
  user: {
    _id: string;
    profileImg: string;
    firstName: string;
    lastName: string;
    username: string;
    isFollowing?: boolean;
    bio: string;
    suggestedTips?: number[];
    minimumTipAmount?: number;
  };
  content: Array<{
    mediaUrl: string;
    paidContent: boolean;
    type: string;
    isBlurred?: boolean;
  }>;
  commentsCount?: number;
  createdAt: string;
  updatedAt: string;
  pinnedAt?: string | null;
}

const placeholderImage =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAHAwGAk6K4FgAAAABJRU5ErkJggg==";

const PAID_POST_TYPES = new Set([
  "Fixed price video",
  "Fixed price image",
  "Bundle",
]);

const isPaidPostType = (type?: string) => PAID_POST_TYPES.has(type ?? "");

const clampPostPrice = (value: number) => Math.max(0, Math.min(9999, value));

const formatPostPriceInput = (value: number) =>
  `$${clampPostPrice(value).toFixed(2)}`;

const parsePostPriceInput = (rawValue: string) => {
  const centsOnly = rawValue.replace(/\D/g, "");
  if (!centsOnly) return 0;
  return clampPostPrice(Number(centsOnly) / 100);
};

interface CreatorPostProps {
  visionType: string;
  customFetchData?: (page: number) => Promise<{
    items: any[];
    totalCount: number;
    totalPages: number;
    message?: string;
  }>;
  selectedCategoryId?: string | null;
  firstPostUsernameRef?: any; // Ref для першого поста (використовується any для підтримки різних типів HTML елементів)
  notFoundText?: string;
}

export interface CreatorPostRef {
  refreshFeed: () => Promise<void>;
}

const CreatorPost = forwardRef<CreatorPostRef, CreatorPostProps>(
  (
    {
      visionType,
      customFetchData,
      selectedCategoryId,
      firstPostUsernameRef,
      notFoundText = "posts",
    },
    ref
  ) => {
    const classes = useCreatorPostStyles();
    const classesForComments = useProfileStyles({ variant: "viewer" });
    const likeAnimation = likeAnimStyle();
    const history = useHistory();
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isLikesOrBookmarks = location.pathname === "/liked-posts";
    const lastPostRef = useRef<HTMLElement | undefined>(undefined);
    const firstPostRef = useRef<HTMLElement | undefined>(undefined);
    const inView = useInViewPort(lastPostRef, { threshold: 0.5 });
    const abortControllerRef = useRef<AbortController | null>(null);
    const [isFollowing, setIsFollowing] = useState<{
      [key: string]: FollowStatus;
    }>({});
    const [posts, setPosts] = useState<Post[]>([]);
    const [selectedPost, setSelectedPost] = useState<Post | null>(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [initialShowComments, setInitialShowComments] = useState(false);
    const [page, setPage] = useState<number>(1);
    const [isLoading, setLoading] = useState<boolean>(false);
    const [hasMore, setHasMore] = useState<boolean>(true);
    const [, setTotalPages] = useState<number>(0);
    // const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    // const [commentText, setCommentText] = useState<string>("");
    const [emojiOpenFor, setEmojiForPost] = useState<string | null>(null); // який пост відкрив пікер
    const [commentTexts, setCommentTexts] = useState<Record<string, string>>(
      {}
    ); // текст комента по постах
    const commentInputRefs = useRef<Record<string, HTMLInputElement | null>>(
      {}
    );
    const commentInputRef = useRef<HTMLInputElement | null>(null);
    const [showBigLikeIndex, setShowBigLikeIndex] = useState<string>("");
    const [isAnimating, setIsAnimating] = useState<boolean>(false);
    const [mediaUrls, setMediaUrls] = useState<{ [key: string]: string }>({});
    const [loadingMedia, setLoadingMedia] = useState<{
      [key: string]: boolean;
    }>({});
    const fetchedUrlsRef = useRef<Set<string>>(new Set());
    const fetchedPostIds = useRef<Set<string>>(new Set());
    const [tipDialogVisible, setTipDialogVisible] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
    const [profileDataMap, setProfileDataMap] = useState<
      Record<string, { suggestedTips: number[]; minimumTipAmount: number }>
    >({});
    const [openCommentsByPost, setOpenCommentsByPost] = useState<
      Record<string, boolean>
    >({});
    const [purchasePostDialogVisible, setPurchasePostDialogVisible] =
      useState(false);
    const [selectedPostForPurchase, setSelectedPostForPurchase] =
      useState<Post | null>(null);
    const [commentsBottomSheetOpen, setCommentsBottomSheetOpen] =
      useState(false);
    const [selectedPostForComments, setSelectedPostForComments] =
      useState<Post | null>(null);
    const [commentText, setCommentText] = useState("");
    const [commentsByPost, setCommentsByPost] = useState<Record<string, any[]>>(
      {}
    );
    const [hasMoreByPost, setHasMoreByPost] = useState<Record<string, boolean>>(
      {}
    );
    const [loadingByPost, setLoadingByPost] = useState<Record<string, boolean>>(
      {}
    );
    const [pageByPost, setPageByPost] = useState<Record<string, number>>({});
    const [showReplies, setShowReplies] = useState<{ [key: string]: boolean }>(
      {}
    );

    //Show only the first comment until "show more" is clicled
    const [expandedPosts, setExpandedPosts] = useState<Record<string, boolean>>(
      {}
    );

    const [showEmojiPickerMobile, setShowEmojiPickerMobile] = useState(false);
    const [tag, setTag] = useState<string | null>(null);
    const [sort, setSort] = useState<string | null>(null);
    const [menuPost, setMenuPost] = useState<Post | null>(null);

    const [shareModalOpen, setShareModalOpen] = useState(false);
    const [shareData, setShareData] = useState<{
      url: string;
      title: string;
      text: string;
      imgUrl: string;
    } | null>(null);

    const [editPostDialogVisible, setEditPostDialogVisible] = useState(false);
    const [selectedPostForEdit, setSelectedPostForEdit] = useState<Post | null>(
      null
    );
    const [editDescription, setEditDescription] = useState("");
    const [editHashtags, setEditHashtags] = useState<string[]>([]);
    const [editPrice, setEditPrice] = useState(0);
    const [editPriceInput, setEditPriceInput] = useState("$0.00");
    const [rawHashtagInput, setRawHashtagInput] = useState("");
    const [isSavingPost, setIsSavingPost] = useState(false);

    // Track swipe gestures to prevent modal opening on swipe
    const swipeStartRef = useRef<{
      x: number;
      y: number;
      postId: string;
    } | null>(null);
    const isSwipingRef = useRef<boolean>(false);

    const { hasPremiumSubscription } = useGlobalMonetization();
    const me = useAppSelector<UserInterface | null>(
      state => state.user.currentUser
    );
    const isValidFileId = (fileId: string): boolean => {
      return /^[0-9a-fA-F]{24}$/.test(fileId);
    };

    const fetchMediaUrl = async (
      mediaUrl: string,
      type: string = "image",
      isAvatar: boolean = false
    ): Promise<string> => {
      if (!mediaUrl || typeof mediaUrl !== "string") {
        setLoadingMedia(prev => ({ ...prev, [mediaUrl]: false }));
        showToast("Invalid media URL", "error");
        return placeholderImage;
      }
      // Якщо це Google URL (lh3.googleusercontent.com), використовуємо його напряму
      if (mediaUrl.includes("lh3.googleusercontent.com")) {
        setLoadingMedia(prev => ({ ...prev, [mediaUrl]: false }));
        setMediaUrls(prev => ({ ...prev, [mediaUrl]: mediaUrl }));
        return mediaUrl;
      }
      // Якщо це пряме посилання (https:// або http://), використовуємо його напряму
      if (mediaUrl.startsWith("https://") || mediaUrl.startsWith("http://")) {
        setLoadingMedia(prev => ({ ...prev, [mediaUrl]: false }));
        setMediaUrls(prev => ({ ...prev, [mediaUrl]: mediaUrl }));
        return mediaUrl;
      }
      if (!mediaUrl.startsWith("/api/files/")) {
        setLoadingMedia(prev => ({ ...prev, [mediaUrl]: false }));
        return mediaUrl;
      }
      const fileId = mediaUrl.split("/api/files/")[1];
      if (!fileId || !isValidFileId(fileId)) {
        setLoadingMedia(prev => ({ ...prev, [mediaUrl]: false }));
        showToast("Invalid file ID", "error");
        return placeholderImage;
      }
      if (fetchedUrlsRef.current.has(mediaUrl) && mediaUrls[mediaUrl]) {
        setLoadingMedia(prev => ({ ...prev, [mediaUrl]: false }));
        return mediaUrls[mediaUrl] || placeholderImage;
      }
      setLoadingMedia(prev => ({ ...prev, [mediaUrl]: true }));
      fetchedUrlsRef.current.add(mediaUrl);
      try {
        const blobUrl = await getFileByIdAPI(fileId);
        if (
          !blobUrl ||
          typeof blobUrl !== "string" ||
          !blobUrl.startsWith("blob:")
        ) {
          throw new Error("Invalid response from getFileByIdAPI");
        }
        setMediaUrls(prev => {
          const newUrls = { ...prev, [mediaUrl]: blobUrl };
          setTimeout(() => {
            const currentUrls = Object.keys(newUrls);
            Object.keys(prev).forEach(key => {
              if (!currentUrls.includes(key) && prev[key].startsWith("blob:")) {
                URL.revokeObjectURL(prev[key]);
              }
            });
          }, 500);
          return newUrls;
        });
        return blobUrl;
      } catch (err) {
        console.error(
          `Error fetching ${isAvatar ? "avatar" : "media"} for ${mediaUrl}:`,
          err
        );
        showToast(`Failed to load ${isAvatar ? "avatar" : type}`, "error");
        return placeholderImage;
      } finally {
        setLoadingMedia(prev => ({ ...prev, [mediaUrl]: false }));
      }
    };

    const handleCloseDialog = () => {
      setDialogOpen(false);
      setSelectedPost(null);
      setInitialShowComments(false);
      const scrollPosition =
        (location.state as any)?.scrollPosition ||
        Number(sessionStorage.getItem("myFeedScrollPosition")) ||
        0;
      history.replace({
        pathname: history.location.pathname,
        search: `?visionType=${visionType}`,
        state: {
          visionType,
          scrollPosition,
        },
      });
    };

    const handlePurchasePost = (post: Post) => {
      setSelectedPostForPurchase(post);
      setPurchasePostDialogVisible(true);
    };

    const applyUnlockedPostToFeed = (post: ProfileViewerPost) => {
      setPosts(prevPosts =>
        prevPosts.map(p =>
          p._id === post._id
            ? {
                ...p,
                access: true,
                content: p.content.map(c => ({
                  ...c,
                  isBlurred: false,
                })),
              }
            : p
        )
      );
      showToast(
        `Successfully unlocked ${post.content[0]?.type === "video" ? "video" : "image"}!`,
        "success"
      );
      setSelectedPostForPurchase(null);
      setPurchasePostDialogVisible(false);
    };

    const handleCompletePurchase = () => {
      if (!selectedPostForPurchase) {
        showToast("No post selected for purchase.", "error");
        return;
      }
      applyUnlockedPostToFeed(selectedPostForPurchase);
    };

    const handlePurchaseSuccess = () => {
      if (!selectedPostForPurchase) return;
      try {
        applyUnlockedPostToFeed(selectedPostForPurchase);
      } catch (err) {
        showToast("Error completing purchase.", "error");
        console.error("Purchase error:", err);
      }
    };

    const preparePosts = (data: {
      items: any[];
      totalCount: number;
      totalPages: number;
      message?: string;
    }) => {
      if (!data?.items || data.items.length === 0) {
        if (data?.message === "You are not following anyone") {
          setPosts([]);
          setHasMore(false);
          setLoading(false);
          showToast(data.message, "info");
          return;
        }
        setHasMore(data.totalPages > page);
        setLoading(false);
        return;
      }
      setTotalPages(data.totalPages);
      const newItems = data.items.filter(
        (item: any) => !fetchedPostIds.current.has(item._id)
      );
      if (newItems.length === 0) {
        setHasMore(data.totalPages > page);
        setLoading(false);
        return;
      }
      newItems.forEach((item: any) => fetchedPostIds.current.add(item._id));
      const preparedPosts: Post[] = newItems.map((item: any) => ({
        _id: item._id,
        userId: item.userId || "",
        description: item.description || "",
        hashtags: item.hashtags || [],
        purchasesCount: item.purchasesCount || 0,
        viewsCount: item.viewsCount || 0,
        likesCount: item.likesCount || 0,
        sharesCount: item.sharesCount || 0,
        isLiked: item.isLiked || false,
        isBookmarked: item.isBookmarked ?? false,
        access: item.access ?? true,
        subscriptionType: item.subscriptionType ?? null,
        hasCreatorSubscription: item.hasCreatorSubscription ?? false,
        creatorSubscriptionTier: item.creatorSubscriptionTier ?? null,
        price: item.price || 0,
        type: item.type || "post",
        // API може повертати різні прапорці: isSubscribed / hasSubscribe / isSubs
        // і як на корені item, так і всередині item.user.
        isSubscribed: Boolean(
          item?.isSubscribed ||
          item?.hasSubscribe ||
          item?.isSubs ||
          item?.user?.isSubscribed ||
          item?.user?.hasSubscribe ||
          item?.user?.isSubs
        ),
        user: {
          _id: item.user?._id || item.userId || "",
          profileImg: item.user?.profileImg || placeholderImage,
          firstName: item.user?.firstName || "Unknown",
          lastName: item.user?.lastName || "Unknown",
          username: item.user?.username || "Unknown",
          bio: item.user?.bio || "Unknown",
          isFollowing: item.user?.isFollowing || false,
        },
        content: (item.content || []).map((c: any) => ({
          mediaUrl: c.mediaUrl || "",
          paidContent: c.paidContent || false,
          type: c.type || "image",
          isBlurred: c.isBlurred ?? false,
        })),
        commentsCount: item.commentsCount || 0,
        createdAt: item.createdAt || new Date().toISOString(),
        updatedAt: item.updatedAt || new Date().toISOString(),
      }));
      const newIsFollowing = { ...isFollowing };
      newItems.forEach(item => {
        const userId = item.user?._id || item.userId;
        if (!userId) return;

        // Check follow status from follow object or isFollowing flag
        let followStatus = FollowStatus.NOT_FOLLOWING;
        if (item.user?.follow) {
          followStatus = item.user.follow.accepted
            ? FollowStatus.FOLLOWING
            : FollowStatus.PENDING;
        } else if (item.user?.isFollowing) {
          followStatus = FollowStatus.FOLLOWING;
        }
        newIsFollowing[userId] = followStatus;
      });
      setIsFollowing(newIsFollowing);
      setPosts(prevPosts => {
        const postIds = new Set(prevPosts.map(p => p._id));
        const uniquePosts = preparedPosts.filter(p => !postIds.has(p._id));
        const newPosts = [...prevPosts, ...uniquePosts];
        uniquePosts.forEach((post: Post) => {
          if (post.user.profileImg && !loadingMedia[post.user.profileImg]) {
            fetchMediaUrl(post.user.profileImg, "image", true);
          }
          post.content.forEach(
            (contentItem: { mediaUrl: string; type: string }) => {
              if (contentItem.mediaUrl && !loadingMedia[contentItem.mediaUrl]) {
                fetchMediaUrl(contentItem.mediaUrl, contentItem.type);
              }
            }
          );
        });
        return newPosts;
      });
      setHasMore(data.totalPages > page && data.items.length > 0);
      setLoading(false);
    };

    const fetchData = async (currentPage: number) => {
      if (isLoading) {
        return;
      }
      setLoading(true);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();
      try {
        let data;
        if (customFetchData) {
          // Use custom fetch function if provided
          data = await customFetchData(currentPage);
        } else if (visionType === "Explore") {
          const params: any = { page: currentPage, perPage: 4, tag, sort };
          if (selectedCategoryId) {
            params.categories = selectedCategoryId;
          }
          data = await getExploreAPI(params, abortControllerRef.current.signal);
        } else if (visionType === "My Subscription") {
          const params: any = { page: currentPage, perPage: 4, sort };
          if (selectedCategoryId) {
            params.categories = selectedCategoryId;
          }
          data = await getMyFeedAPI(params, abortControllerRef.current.signal);
        } else {
          setLoading(false);
          showToast(`Invalid vision type: ${visionType}`, "error");
          return;
        }
        preparePosts(data);
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") {
          return;
        }
        //  console.error("Error fetching posts:", err);
        //  const errorMessage = err instanceof Error ? err.message : "Failed to load posts";
        // showToast(errorMessage, "error");
        setHasMore(false);
        setLoading(false);
      } finally {
        setLoading(false);
      }
    };

    // Function to refresh the feed and clear error messages
    const refreshFeed = async () => {
      // Clear any existing error states
      setHasMore(true);
      setLoading(false);

      // Reset to first page and fetch fresh data
      setPage(1);
      setPosts([]);
      fetchedPostIds.current.clear();
      fetchedUrlsRef.current.clear();
      setMediaUrls({});
      setLoadingMedia({});
      setCommentsByPost({});

      // Fetch fresh data
      await fetchData(1);
    };

    // Expose refreshFeed via ref
    useImperativeHandle(ref, () => ({
      refreshFeed,
    }));

    // Handle post deletion callback
    const handlePostDeleted = (deletedPostId: string) => {
      // Remove the deleted post from the current posts list
      setPosts(prevPosts =>
        prevPosts.filter(post => post._id !== deletedPostId)
      );

      // Clear any error messages by showing success feedback
      showToast("Post deleted successfully", "success");

      // Refresh the feed to ensure consistency
      refreshFeed();
    };

    const addEditHashtag = (tag: string) => {
      const cleanTag = tag.trim().replace(/^#+/, "").toLowerCase();
      if (
        cleanTag &&
        !editHashtags.includes(cleanTag) &&
        editHashtags.length < 5
      ) {
        setEditHashtags(prev => [...prev, cleanTag]);
        setRawHashtagInput("");
      }
    };

    const removeEditHashtag = (tagToRemove: string) => {
      setEditHashtags(prev => prev.filter(tag => tag !== tagToRemove));
    };

    const handleEditHashtagKeyPress = (
      e: React.KeyboardEvent<HTMLInputElement>
    ) => {
      if (e.key === "Enter" || e.key === "Return") {
        e.preventDefault();
        if (rawHashtagInput.trim()) {
          addEditHashtag(rawHashtagInput);
        }
      }
    };

    const handleEditHashtagInputChange = (
      e: React.ChangeEvent<HTMLInputElement>
    ) => {
      setRawHashtagInput(e.target.value);
    };

    const handleEditPost = (post: ProfileViewerPost) => {
      const postToEdit: Post =
        selectedPost && selectedPost._id === post._id
          ? selectedPost
          : (post as unknown as Post);
      setSelectedPostForEdit(postToEdit);
      setEditDescription(postToEdit.description || "");
      const hashtags = postToEdit.hashtags || [];
      setEditHashtags(
        hashtags
          .map(tag => {
            const cleaned = tag.replace(/^#+/, "").trim().toLowerCase();
            return cleaned;
          })
          .filter(Boolean)
      );
      setRawHashtagInput("");
      const initialPrice = clampPostPrice(postToEdit.price || 0);
      setEditPrice(initialPrice);
      setEditPriceInput(formatPostPriceInput(initialPrice));
      setEditPostDialogVisible(true);
    };

    const handleSavePostEdit = async () => {
      if (!selectedPostForEdit) return;

      const isPaidPost = isPaidPostType(selectedPostForEdit.type);

      if (
        editDescription.trim().length < 1 ||
        editDescription.trim().length > 2000
      ) {
        showToast("Description must be between 1 and 2000 characters", "error");
        return;
      }

      if (isPaidPost && (editPrice < 0 || editPrice > 9999)) {
        showToast("Price must be between 0 and 9999", "error");
        return;
      }

      const isPrivatePost = isPaidPost || editPrice > 0;
      if (isPrivatePost && editPrice > 0 && editPrice < 5) {
        showToast("Minimum price for private posts is $5.00", "error");
        return;
      }

      if (editHashtags.length > 5) {
        showToast("Maximum 5 hashtags allowed", "error");
        return;
      }

      setIsSavingPost(true);
      try {
        const payload: {
          description: string;
          hashtags: string[];
          price?: number;
        } = {
          description: editDescription.trim(),
          hashtags: editHashtags,
        };
        if (isPaidPost) {
          payload.price = editPrice;
        }

        const updatedPrice = isPaidPost
          ? editPrice
          : clampPostPrice(selectedPostForEdit.price || 0);

        await updateGalleryPostAPI(selectedPostForEdit._id, {
          ...payload,
        });

        setPosts(prev =>
          prev.map(p =>
            p._id === selectedPostForEdit._id
              ? {
                  ...p,
                  description: editDescription.trim(),
                  hashtags: editHashtags,
                  price: updatedPrice,
                }
              : p
          )
        );

        if (selectedPost && selectedPost._id === selectedPostForEdit._id) {
          setSelectedPost({
            ...selectedPost,
            description: editDescription.trim(),
            hashtags: editHashtags,
            price: updatedPrice,
          });
        }

        setSelectedPostForEdit({
          ...selectedPostForEdit,
          description: editDescription.trim(),
          hashtags: editHashtags,
          price: updatedPrice,
        });

        showToast("Post updated successfully", "success");
        setEditPostDialogVisible(false);
        setSelectedPostForEdit(null);
        setRawHashtagInput("");
      } catch (error: any) {
        console.error("Error updating post:", error);
        showToast(
          error?.response?.data?.message || "Failed to update post",
          "error"
        );
      } finally {
        setIsSavingPost(false);
      }
    };

    useEffect(() => {
      setPosts([]);
      setPage(1);
      setHasMore(true);
      setTotalPages(0);
      fetchedUrlsRef.current.clear();
      fetchedPostIds.current.clear();
      setMediaUrls({});
      setLoadingMedia({});
      setIsFollowing({});

      const initializeFollowStatus = async () => {
        const uniqueUserIds = new Set(posts.map(p => p.userId));
        const profilePromises = Array.from(uniqueUserIds).map(async userId => {
          try {
            const profileData = await getUserProfile(userId);
            return { userId, follow: profileData.follow };
          } catch (err) {
            console.error("Error fetching profile:", err);
            return { userId, follow: null };
          }
        });
        const profiles = await Promise.all(profilePromises);
        const initialFollowStatus = profiles.reduce(
          (acc, { userId, follow }) => {
            acc[userId] = follow
              ? follow.accepted
                ? FollowStatus.FOLLOWING
                : FollowStatus.PENDING
              : FollowStatus.NOT_FOLLOWING;
            return acc;
          },
          {} as { [key: string]: FollowStatus }
        );
        setIsFollowing(initialFollowStatus);
      };

      initializeFollowStatus().then(() => {
        const timeout = setTimeout(() => {
          fetchData(1);
        }, 100);
        return () => clearTimeout(timeout);
      });

      return () => {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visionType, tag, sort, selectedCategoryId]);

    useEffect(() => {
      if (!inView || !hasMore || isLoading) {
        return;
      }
      setPage(prev => {
        const nextPage = prev + 1;
        fetchData(nextPage);
        return nextPage;
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inView, hasMore, isLoading]);

    useEffect(() => {
      const savedScroll = sessionStorage.getItem("myFeedScrollPosition");
      if (savedScroll && posts.length > 0) {
        setTimeout(() => {
          window.scrollTo({ top: Number(savedScroll) }); // без behavior
          sessionStorage.removeItem("myFeedScrollPosition");
        }, 100);
      }
    }, [posts]);

    useEffect(() => {
      const fetchProfiles = async () => {
        const newUserIds = posts
          .map(p => p.userId)
          .filter(id => !profileDataMap[id]);
        if (newUserIds.length === 0) return;
        const profilePromises = newUserIds.map(async userId => {
          try {
            const profileData = await getUserProfile(userId);
            return {
              userId,
              suggestedTips: profileData.suggestedTips || [5, 10, 15, 20, 25],
              minimumTipAmount: profileData.minimumTipAmount || 2,
            };
          } catch (err) {
            return {
              userId,
              suggestedTips: [5, 10, 15, 20, 25],
              minimumTipAmount: 2,
            };
          }
        });
        const fetchedProfiles = await Promise.all(profilePromises);
        const updatedMap = { ...profileDataMap };
        fetchedProfiles.forEach(
          ({ userId, suggestedTips, minimumTipAmount }) => {
            updatedMap[userId] = { suggestedTips, minimumTipAmount };
          }
        );
        setProfileDataMap(updatedMap);
      };

      fetchProfiles();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [posts]);

    useEffect(() => {
      const searchParams = new URLSearchParams(location.search);
      const postId = searchParams.get("postId");
      const tag = searchParams.get("tag");
      // Спочатку перевіряємо URL параметри
      const urlSortBy = searchParams.get("sortBy") as
        | "popular"
        | "recent"
        | null;
      // Потім перевіряємо localStorage
      const savedSortBy = localStorage.getItem("sortBy") as
        | "popular"
        | "recent"
        | null;

      const sortParam = urlSortBy || savedSortBy || "popular";
      setTag(tag);
      setSort(sortParam);
      if (postId && posts.length && !dialogOpen) {
        const post = posts.find(p => p._id === postId);
        if (post) {
          const scrollY = window.scrollY.toString();
          if (!sessionStorage.getItem("myFeedScrollPosition")) {
            sessionStorage.setItem("myFeedScrollPosition", scrollY);
          }
          setSelectedPost(post);
          setInitialShowComments(!!(location.state as any)?.openComments);
          setDialogOpen(true);
        }
      }
    }, [location.search, posts, dialogOpen]);

    const handleProfileClick = (userId: string) => {
      const scrollPosition = window.scrollY;
      sessionStorage.setItem("myFeedScrollPosition", scrollPosition.toString());
      history.push({
        pathname: `/${userId}`,
        state: {
          fromPost: true,
          previousPath: location.pathname + location.search,
          visionType,
        },
      });
    };

    const likeClick = (postId: string, idx: number) => {
      likeGalleryPostAPI(postId)
        .then(() => {
          setPosts(prevPosts => {
            const newPosts = [...prevPosts];
            const post = newPosts[idx];
            if (post) {
              post.isLiked = !post.isLiked;
              post.likesCount += post.isLiked ? 1 : -1;
            }
            return newPosts;
          });
        })
        .catch(() => showToast("Failed to like post", "error"));
    };

    const bigLikeHandler = (postId: string, idx: number) => {
      setShowBigLikeIndex(postId);
      setIsAnimating(true);
      likeClick(postId, idx);
      setTimeout(() => setIsAnimating(false), 500);
      setTimeout(() => setShowBigLikeIndex(""), 1000);
    };

    const updateBookmarkState = (postId: string, isBookmarked: boolean) => {
      setPosts(prev =>
        prev.map(post =>
          post._id === postId ? { ...post, isBookmarked } : post
        )
      );
      setSelectedPost(prev =>
        prev && prev._id === postId ? { ...prev, isBookmarked } : prev
      );
    };

    const handleEmojiSelectFor = (postId: string) => (emoji: any) => {
      const char = emoji?.native || emoji?.unified || "";
      if (!char) return;
      const input = commentInputRefs.current[postId];
      const currentValue = commentTexts[postId] || "";
      if (!input) {
        setCommentTexts(prev => ({
          ...prev,
          [postId]: currentValue + char,
        }));
        return;
      }
      const start = input.selectionStart ?? currentValue.length;
      const end = input.selectionEnd ?? start;
      const nextValue = `${currentValue.slice(0, start)}${char}${currentValue.slice(end)}`;
      setCommentTexts(prev => ({
        ...prev,
        [postId]: nextValue,
      }));
      requestAnimationFrame(() => {
        input.focus();
        const cursor = start + char.length;
        input.setSelectionRange(cursor, cursor);
      });
    };

    const handleSendComment = (postId: string, idx: number) => {
      const text = (commentTexts[postId] || "").trim();
      if (!text) return;

      // Rate limiting: 3 comments per minute
      const rateLimitKey = "comment_rate_limit";
      const rateLimitResult = checkRateLimit(rateLimitKey, 3, 60000);
      if (!rateLimitResult.allowed) {
        const remainingSeconds = Math.ceil(rateLimitResult.remainingMs / 1000);
        showToast(
          `Too many comments. Please wait ${remainingSeconds} seconds.`,
          "error"
        );
        return;
      }

      // якщо пост залочений — не даємо коментувати (якщо треба)
      const p = posts[idx];
      if (!p?.access) {
        showToast("Unlock this post to comment", "info");
        return;
      }

      sendCommentToPostAPI(text, { galleryPostId: postId })
        .then(() => {
          setPosts(prev => {
            const clone = [...prev];
            clone[idx].commentsCount = (clone[idx].commentsCount || 0) + 1;
            return clone;
          });
          setCommentTexts(prev => ({ ...prev, [postId]: "" }));
          showToast("Comment sent", "success");
        })
        .catch(() => showToast("Failed to send comment", "error"));
    };

    const fetchCommentsForPost = async (
      post: Post,
      pageNum = 1,
      append = false,
      customPerPage?: number
    ) => {
      if (!post) return;
      // if (
      //   !post ||
      //   !post.access ||
      //   me?.id === post.userId ||
      //   me?._id === post.userId ||
      //   me?.id === post.user._id ||
      //   me?._id === post.user._id
      // )
      //   return;

      //Always the same perPage
      const COMMENTS_PER_PAGE = customPerPage ?? 10;

      setLoadingByPost(prev => ({ ...prev, [post._id]: true }));

      try {
        const res = await getUserGalleryPostCommentsAPI(
          post.userId,
          post._id,
          null,
          { page: pageNum, perPage: COMMENTS_PER_PAGE }
        );

        const items = (res as any).items || res;
        const total =
          (res as any).total ??
          (res as any).totalCount ??
          post.commentsCount ??
          items.length;

        const getComment = (comment: any): any[] =>
          comment.map((c: any) => ({
            id: c.id,
            text: c.comment,
            hashtags: c.hashtags || [],
            createdAt: c.createdAt,
            isLiked: c.isLiked,
            likesCount: c.likesCount,
            repliesCount: c.repliesCount || 0,
            replyToCommentId: c.replyToCommentId || null,
            user: {
              id: c.user.id,
              username: c.user.username,
              firstName: c.user.firstName,
              lastName: c.user.lastName,
              profileImg: c.user.profileImg,
            },
            replies: c.replies ? getComment(c.replies) : [],
            status: c.status ?? null,
          }));

        const newComments = getComment(items);
        const existingComments = append ? commentsByPost[post._id] || [] : [];

        const existingIds = new Set(existingComments.map((c: any) => c.id));
        const uniqueNewComments = newComments.filter(
          (c: any) => !existingIds.has(c.id)
        );

        const allLoaded = [...existingComments, ...uniqueNewComments];

        setCommentsByPost(prev => ({ ...prev, [post._id]: allLoaded }));
        setPageByPost(prev => ({ ...prev, [post._id]: pageNum }));

        const totalPages = (res as any).totalPages;
        const currentPage = (res as any).currentPage ?? pageNum;
        const hasMore =
          typeof totalPages === "number" && totalPages > 0
            ? currentPage < totalPages
            : allLoaded.length < total;

        setHasMoreByPost(prev => ({
          ...prev,
          [post._id]: hasMore,
        }));
      } catch (err) {
        console.error("Error fetching comments:", err);
        showToast("Error fetching comments", "error");
      } finally {
        setLoadingByPost(prev => ({ ...prev, [post._id]: false }));
      }
    };

    const loadMoreComments = (post: Post) => {
      if (!post || loadingByPost[post._id] || !hasMoreByPost[post._id]) return;
      const nextPage = (pageByPost[post._id] || 1) + 1;
      fetchCommentsForPost(post, nextPage, true);
    };

    const handleSendCommentForBottomSheet = async () => {
      if (!selectedPostForComments) return;
      const text = commentText.trim();
      if (!text) return;

      // Rate limiting: 3 comments per minute
      const rateLimitKey = "comment_rate_limit";
      const rateLimitResult = checkRateLimit(rateLimitKey, 3, 60000);
      if (!rateLimitResult.allowed) {
        const remainingSeconds = Math.ceil(rateLimitResult.remainingMs / 1000);
        showToast(
          `Too many comments. Please wait ${remainingSeconds} seconds.`,
          "error"
        );
        return;
      }

      try {
        await sendCommentToPostAPI(text, {
          galleryPostId: selectedPostForComments._id,
        });
        setCommentText("");
        // Оновлюємо кількість коментарів в пості
        setPosts(prev => {
          const clone = [...prev];
          const postIdx = clone.findIndex(
            p => p._id === selectedPostForComments._id
          );
          if (postIdx !== -1) {
            clone[postIdx].commentsCount =
              (clone[postIdx].commentsCount || 0) + 1;
          }
          return clone;
        });
        // Перезавантажуємо коментарі
        await fetchCommentsForPost(selectedPostForComments);
        showToast("Comment sent", "success");
      } catch (err) {
        showToast("Failed to send comment", "error");
      }
    };

    const loadRepliesForComment = async (post: Post, commentId: string) => {
      // Рекурсивно рахує всі replies включаючи вкладені
      const countAllReplies = (replies: any[]): number => {
        return replies.reduce((acc, reply) => {
          return acc + 1 + countAllReplies(reply.replies || []);
        }, 0);
      };

      try {
        const res = await getUserGalleryPostCommentsAPI(
          post.userId,
          post._id,
          commentId as any,
          { page: 1, perPage: 50 }
        );
        const items = (res as any).items || res;

        //  для кожного reply завантажуємо його replies якщо є
        const repliesWithNested = await Promise.all(
          items.map(async (c: any) => {
            let nestedReplies: any[] = [];
            if (c.repliesCount > 0) {
              try {
                const nestedRes = await getUserGalleryPostCommentsAPI(
                  post.userId,
                  post._id,
                  c.id,
                  { page: 1, perPage: 50 }
                );
                nestedReplies = (nestedRes as any).items || [];
              } catch (e) {
                console.error("Error loading nested replies", e);
              }
            }
            return {
              id: c.id,
              text: c.comment,
              hashtags: c.hashtags || [],
              createdAt: c.createdAt,
              isLiked: c.isLiked,
              likesCount: c.likesCount,
              repliesCount: c.repliesCount || 0,
              replyToCommentId: c.replyToCommentId,
              user: {
                id: c.user.id,
                username: c.user.username,
                firstName: c.user.firstName,
                lastName: c.user.lastName,
                profileImg: c.user.profileImg,
              },
              replies: nestedReplies.map((r: any) => ({
                id: r.id,
                text: r.comment,
                hashtags: r.hashtags || [],
                createdAt: r.createdAt,
                isLiked: r.isLiked,
                likesCount: r.likesCount,
                repliesCount: r.repliesCount || 0,
                replyToCommentId: r.replyToCommentId,
                user: {
                  id: r.user.id,
                  username: r.user.username,
                  firstName: r.user.firstName,
                  lastName: r.user.lastName,
                  profileImg: r.user.profileImg,
                },
                replies: [],
              })),
            };
          })
        );

        const repliesCountAllWithNested = countAllReplies(repliesWithNested);
        setCommentsByPost(prev => ({
          ...prev,
          [post._id]: (prev[post._id] || []).map((c: any) =>
            c.id === commentId
              ? {
                  ...c,
                  replies: repliesWithNested,
                  repliesCountAllWithNested,
                }
              : c
          ),
        }));
      } catch (err) {
        console.error("Error loading replies:", err);
      }
    };

    const toggleReplies = (commentId: string, repliesCount: number = 0) => {
      setShowReplies(prevState => {
        const isOpening = !prevState[commentId];
        if (isOpening && repliesCount > 0) {
          if (selectedPostForComments) {
            loadRepliesForComment(selectedPostForComments, commentId);
          }
        }
        return { ...prevState, [commentId]: isOpening };
      });
    };

    const handleEmojiSelectMobile = (emoji: any) => {
      const char = emoji?.native || emoji?.unified || "";
      if (!char) return;
      const input = commentInputRef.current;
      if (!input) {
        setCommentText(prev => prev + char);
        setShowEmojiPickerMobile(false);
        return;
      }
      const start = input.selectionStart ?? commentText.length;
      const end = input.selectionEnd ?? start;
      const nextValue = `${commentText.slice(0, start)}${char}${commentText.slice(end)}`;
      setCommentText(nextValue);
      requestAnimationFrame(() => {
        input.focus();
        const cursor = start + char.length;
        input.setSelectionRange(cursor, cursor);
      });
      setShowEmojiPickerMobile(false);
    };

    // RenderAvatar for mobile comments
    const RenderAvatar = ({
      profileImg,
      style,
      onClick,
    }: {
      profileImg: string;
      style?: React.CSSProperties;
      onClick?: () => void;
    }) => {
      if (!profileImg) {
        return <Avatar src={videoIconSvg} style={style} onClick={onClick} />;
      }
      if (loadingMedia[profileImg]) {
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              ...style,
            }}
          >
            <CircularProgress size={24} />
          </div>
        );
      }
      return (
        <Avatar
          src={mediaUrls[profileImg] || videoIconSvg}
          style={style}
          onClick={onClick}
        />
      );
    };

    // Load avatar media for comments
    useEffect(() => {
      const allComments = Object.values(commentsByPost).flat();
      if (allComments.length === 0) return;

      allComments.forEach(comment => {
        if (
          comment.user.profileImg &&
          !fetchedUrlsRef.current.has(comment.user.profileImg)
        ) {
          fetchedUrlsRef.current.add(comment.user.profileImg);
          fetchMediaUrl(comment.user.profileImg, "image", true);
        }
        comment.replies?.forEach((reply: any) => {
          if (
            reply.user.profileImg &&
            !fetchedUrlsRef.current.has(reply.user.profileImg)
          ) {
            fetchedUrlsRef.current.add(reply.user.profileImg);
            fetchMediaUrl(reply.user.profileImg, "image", true);
          }
        });
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [commentsByPost]);

    useEffect(() => {
      if (!posts || posts.length === 0) return;

      // Load only 1 comment for each new post
      const newPosts = posts.filter(p => !commentsByPost[p._id]);
      if (newPosts.length === 0) return;

      Promise.all(newPosts.map(post => fetchCommentsForPost(post, 1, false)));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [posts]);

    const formatCommentTime = (createdAt: string): string => {
      const now = Date.now();
      const commentDate = new Date(createdAt).getTime();
      const diffMs = now - commentDate;
      const diffSeconds = Math.floor(diffMs / 1000);
      const diffMinutes = Math.floor(diffSeconds / 60);
      const diffHours = Math.floor(diffMinutes / 60);
      const diffDays = Math.floor(diffHours / 24);
      const diffWeeks = Math.floor(diffDays / 7);
      const diffMonths = Math.floor(diffDays / 30);
      const diffYears = Math.floor(diffDays / 365);

      if (diffSeconds < 60) return "now";
      if (diffMinutes < 60) return `${diffMinutes}m`;
      if (diffHours < 24) return `${diffHours}h`;
      if (diffDays < 7) return `${diffDays}d`;
      if (diffWeeks < 4) return `${diffWeeks}w`;
      if (diffMonths < 12) return `${diffMonths}m`;
      return `${diffYears}y`;
    };

    const handleFollow = async (userId: string) => {
      try {
        const response = await followUserAPI(userId);
        const follow = response.data || null;
        const newFollowStatus = follow
          ? follow.accepted
            ? FollowStatus.FOLLOWING
            : FollowStatus.PENDING
          : FollowStatus.NOT_FOLLOWING;

        setPosts(prevPosts => {
          return prevPosts.map(post => {
            // Update all posts from this user
            if (post.user._id === userId) {
              const updatedPost = { ...post };
              updatedPost.user.isFollowing =
                newFollowStatus === FollowStatus.FOLLOWING;

              // If successfully followed (not pending), unlock free content
              if (newFollowStatus === FollowStatus.FOLLOWING) {
                // Unlock posts that are free (not paid content)
                if (!post.price || post.price === 0) {
                  updatedPost.access = true;
                  updatedPost.content = updatedPost.content.map(
                    contentItem => ({
                      ...contentItem,
                      isBlurred: false,
                    })
                  );
                }
              } else if (newFollowStatus === FollowStatus.NOT_FOLLOWING) {
                // If unfollowed, lock free content again (but keep paid content access if purchased)
                if (!post.price || post.price === 0) {
                  updatedPost.access = false;
                  updatedPost.content = updatedPost.content.map(
                    contentItem => ({
                      ...contentItem,
                      isBlurred: true,
                    })
                  );
                }
              }
              return updatedPost;
            }
            return post;
          });
        });

        setIsFollowing(prev => ({
          ...prev,
          [userId]: newFollowStatus,
        }));

        // Update menuPost if it's the same user
        setMenuPost(prev => {
          if (prev && prev.user._id === userId) {
            return {
              ...prev,
              user: {
                ...prev.user,
                isFollowing: newFollowStatus === FollowStatus.FOLLOWING,
              },
            };
          }
          return prev;
        });

        // If My Subscription, refresh the feed to show/hide posts based on follow status
        if (visionType === "My Subscription") {
          // Remove posts from unfollowed users or add posts from newly followed users
          if (newFollowStatus === FollowStatus.NOT_FOLLOWING) {
            // Remove posts from this user
            setPosts(prevPosts =>
              prevPosts.filter(post => post.user._id !== userId)
            );
          } else if (newFollowStatus === FollowStatus.FOLLOWING) {
            // Refresh feed to potentially show new posts from this user
            // Use a small delay to ensure state is updated
            setTimeout(() => {
              refreshFeed();
            }, 500);
          }
        }

        if (!follow) {
          showToast("Unfollowed successfully", "success");
        } else if (!follow.accepted) {
          showToast("Follow request sent", "info");
        } else {
          showToast("Followed successfully", "success");
        }
      } catch (err) {
        showToast((err as Error)?.message || "Error while following!", "error");
        console.error("Follow error:", err);
      }
    };

    useEffect(() => {
      if (!isMobile) return;
      const close = () => setEmojiForPost(null);
      window.addEventListener("scroll", close, true);
      window.addEventListener("click", close, true);
      return () => {
        window.removeEventListener("scroll", close, true);
        window.removeEventListener("click", close, true);
      };
    }, [isMobile]);

    // Custom arrow components for slider navigation
    const PrevArrow = ({ onClick }: { onClick?: () => void }) => {
      return (
        <div
          className={`${classes.sliderArrow} ${classes.sliderArrowPrev}`}
          onClick={onClick}
          style={{ display: isMobile ? "none" : "flex" }}
        >
          <ChevronLeft size={24} color="#000" />
        </div>
      );
    };

    const NextArrow = ({ onClick }: { onClick?: () => void }) => {
      return (
        <div
          className={`${classes.sliderArrow} ${classes.sliderArrowNext}`}
          onClick={onClick}
          style={{ display: isMobile ? "none" : "flex" }}
        >
          <ChevronRight size={24} color="#000" />
        </div>
      );
    };

    const sliderSettings = {
      dots: true,
      arrows: false, // Will be set conditionally per post
      speed: 400, // slower speed for smoother feel
      cssEase: "ease-in-out", // apply easing for smooth transition
      fade: false, // optional cross-fade; set true if desired
      slidesToShow: 1,
      slidesToScroll: 1,
      adaptiveHeight: true,
      swipe: true, // Enable swipe on mobile
      touchMove: true, // Enable touch move
      draggable: true, // Enable dragging
      swipeToSlide: true, // Enable swipe to slide
    };

    const openPostDialog = (post: Post, opts?: { showComments?: boolean }) => {
      const isPaidLocked =
        (post.price ?? 0) > 0 &&
        !post.access &&
        !(me?.isAdmin || me?.isSuperAdmin) &&
        me?.id !== post.userId &&
        me?._id !== post.userId &&
        me?.id !== post.user?._id &&
        me?._id !== post.user?._id;

      if (isPaidLocked) {
        if (!me?.id) {
          showToast("You must be logged in to purchase content.", "error");
          return;
        }
        handlePurchasePost(post);
        return;
      }

      const showComments = !!opts?.showComments;
      const scrollPosition = window.scrollY;
      sessionStorage.setItem("myFeedScrollPosition", scrollPosition.toString());

      setSelectedPost(post);
      setInitialShowComments(showComments);
      setDialogOpen(true);

      history.replace({
        pathname: location.pathname,
        search: `?postId=${post._id}&visionType=${encodeURIComponent(visionType)}`,
        state: {
          fromPostDialog: true,
          previousPath: location.pathname + location.search,
          visionType,
          scrollPosition,
          openComments: showComments,
        },
      });
    };

    const handlePostClick = (post: Post, e?: React.MouseEvent) => {
      // Prevent modal opening if user was swiping
      if (isSwipingRef.current) {
        isSwipingRef.current = false;
        return;
      }
      openPostDialog(post, { showComments: false });
    };

    // Handle touch start for swipe detection
    const handleTouchStart = (e: React.TouchEvent, postId: string) => {
      swipeStartRef.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
        postId,
      };
      isSwipingRef.current = false;
    };

    // Handle touch move for swipe detection
    const handleTouchMove = (e: React.TouchEvent) => {
      if (!swipeStartRef.current) return;
      const deltaX = Math.abs(e.touches[0].clientX - swipeStartRef.current.x);
      const deltaY = Math.abs(e.touches[0].clientY - swipeStartRef.current.y);
      // If horizontal movement is greater than vertical, it's a swipe
      if (deltaX > 10 && deltaX > deltaY) {
        isSwipingRef.current = true;
      }
    };

    // Handle touch end - reset swipe detection
    const handleTouchEnd = () => {
      setTimeout(() => {
        isSwipingRef.current = false;
        swipeStartRef.current = null;
      }, 100);
    };

    // Handle mouse down for desktop swipe detection
    const handleMouseDown = (e: React.MouseEvent, postId: string) => {
      swipeStartRef.current = {
        x: e.clientX,
        y: e.clientY,
        postId,
      };
      isSwipingRef.current = false;
    };

    // Handle mouse move for desktop swipe detection
    const handleMouseMove = (e: React.MouseEvent) => {
      if (!swipeStartRef.current) return;
      const deltaX = Math.abs(e.clientX - swipeStartRef.current.x);
      const deltaY = Math.abs(e.clientY - swipeStartRef.current.y);
      // If horizontal movement is greater than vertical, it's a swipe
      if (deltaX > 10 && deltaX > deltaY) {
        isSwipingRef.current = true;
      }
    };

    // Handle mouse up - reset swipe detection
    const handleMouseUp = () => {
      setTimeout(() => {
        isSwipingRef.current = false;
        swipeStartRef.current = null;
      }, 100);
    };

    const handleTipSuccess = (tipAmount: number) => {
      if (selectedUserId) {
        const username = posts.find(p => p.user._id === selectedUserId)?.user
          .username;
        showToast(`Tipped $${tipAmount} to ${username}!`, "success");
        setSelectedUserId(null);
      }
    };

    // const handleEmojiSelect = (emoji: any) => {
    //   setCommentText((prev) => prev + (emoji?.native || emoji?.unified || ""));
    // };

    const handleToggleBookmark = async (post: Post) => {
      const isCurrentlyBookmarked = !!post.isBookmarked;
      try {
        if (isCurrentlyBookmarked) {
          await removeBookmarkAPI(post._id);
          showToast("Removed from bookmarks", "success");
        } else {
          await addBookmarkAPI(post._id);
          showToast("Added to bookmarks", "success");
        }
        updateBookmarkState(post._id, !isCurrentlyBookmarked);
        setMenuPost(prev =>
          prev && prev._id === post._id
            ? { ...prev, isBookmarked: !isCurrentlyBookmarked }
            : prev
        );
      } catch (e) {
        showToast(
          (e as Error)?.message ||
            `Failed to ${isCurrentlyBookmarked ? "remove" : "add"} bookmark`,
          "error"
        );
      }
    };

    // const handleToggleBookmarkFromMenu = async () => {
    //   if (!menuPost) return;
    //   closeMenu();
    //   await handleToggleBookmark(menuPost);
    // };

    const buildShareUrl = (post: Post) =>
      `${window.location.origin}/${post.user._id}?postId=${post._id}`;

    const handleShare = (post: Post) => {
      const url = buildShareUrl(post);
      const shareTitle = `Check out this ${post.type} by ${post.user.username}`;
      const shareText = post.description || shareTitle;
      const profileImg = post.user.profileImg || placeholderImage;

      setShareData({
        url,
        title: shareTitle,
        text: shareText,
        imgUrl: profileImg,
      });
      setShareModalOpen(true);
    };

    // const handleShare = async (post: Post) => {
    //   const url = buildShareUrl(post);
    //   const shareTitle = `Check out this ${post.type === "photo" ? "photo" : post.type === "video" ? "video" : "post"
    //     } by ${post.user.username}`;
    //   const shareText = post.description || shareTitle;

    //   try {
    //     let sharePlatform = "clipboard";

    //     // Check if Web Share API is supported
    //     if (navigator.share && navigator.canShare) {
    //       const shareData: ShareData = {
    //         title: shareTitle,
    //         text: shareText,
    //         url: url,
    //       };

    //       // Check if the data can be shared
    //       if (navigator.canShare(shareData)) {
    //         try {
    //           await navigator.share(shareData);
    //           sharePlatform = "native_share";
    //           showToast("Shared successfully!", "success");
    //         } catch (shareError: any) {
    //           // User cancelled or error occurred
    //           if (shareError.name === "AbortError") {
    //             // User cancelled, don't show error
    //             return;
    //           }
    //           // Fall through to clipboard fallback
    //           throw shareError;
    //         }
    //       } else {
    //         // Can't share with full data, try with just URL
    //         try {
    //           await navigator.share({ url });
    //           sharePlatform = "native_share";
    //           showToast("Shared successfully!", "success");
    //         } catch (shareError: any) {
    //           if (shareError.name === "AbortError") {
    //             return;
    //           }
    //           throw shareError;
    //         }
    //       }
    //     } else {
    //       // Web Share API not supported, use clipboard
    //       if (navigator.clipboard && navigator.clipboard.writeText) {
    //         await navigator.clipboard.writeText(url);
    //         showToast("Link copied to clipboard", "success");
    //         sharePlatform = "clipboard";
    //       } else {
    //         // Fallback for older browsers
    //         const textArea = document.createElement("textarea");
    //         textArea.value = url;
    //         textArea.style.position = "fixed";
    //         textArea.style.left = "-999999px";
    //         document.body.appendChild(textArea);
    //         textArea.focus();
    //         textArea.select();
    //         try {
    //           document.execCommand("copy");
    //           showToast("Link copied to clipboard", "success");
    //           sharePlatform = "clipboard";
    //         } catch (err) {
    //           showToast("Failed to copy link", "error");
    //           return;
    //         } finally {
    //           document.body.removeChild(textArea);
    //         }
    //       }
    //     }

    //     // Track the share
    //     await trackShare(
    //       post._id,
    //       post.type === "photo"
    //         ? "photo"
    //         : post.type === "video"
    //           ? "video"
    //           : "post",
    //       "link",
    //       sharePlatform,
    //       {
    //         shareUrl: url,
    //         shareText: shareText,
    //         shareMetadata: {
    //           postType: post.type,
    //           creatorId: post.user._id,
    //           creatorUsername: post.user.username,
    //         },
    //       }
    //     );

    //     setPosts(prev =>
    //       prev.map(p =>
    //         p._id === post._id
    //           ? { ...p, sharesCount: (p.sharesCount || 0) + 1 }
    //           : p
    //       )
    //     );
    //   } catch (error) {
    //     // console.error("Error sharing post:", error);
    //   }
    // };

    // const handleShareFromMenu = async () => {
    //   if (!menuPost) return;
    //   closeMenu();
    //   await handleShare(menuPost);
    // };

    const handleTipFromMenu = (post: Post) => {
      setSelectedUserId(post.user._id);
      setTipDialogVisible(true);
    };

    // const handleFollowFromMenu = async () => {
    //   if (!menuPost) return;
    //   const userId = menuPost.user._id;
    //   // Don't close menu immediately - let user see the change
    //   await handleFollow(userId);
    //   // Close menu after update with delay to show the change
    //   setTimeout(() => {
    //     closeMenu();
    //   }, 500);
    // };

    const handleCommentsChange = (
      updatedComments: Comment[],
      postId: string
    ) => {
      setCommentsByPost(prev => {
        return {
          ...prev,
          [postId]: updatedComments.map(c => ({
            ...c,
            replies: c.replies ?? [],
          })),
        };
      });
    };

    return (
      <Box
        className={`${classes.wrapper} ${
          visionType === "My Subscription" ? "MyFeed" : "Explore"
        }`}
      >
        {isLoading && !posts.length && (
          <div
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              marginTop: 20,
            }}
          >
            <CircularProgress />
            <Typography style={{ color: "#fff", marginTop: 10 }}>
              Loading posts...
            </Typography>
          </div>
        )}
        {!isLoading && posts.length === 0 && (
          <Box
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "16px",
              minHeight: "65vh",
            }}
          >
            <Box
              style={{
                width: 120,
                height: 120,
                borderRadius: "50%",
                backgroundColor: "#1F1F1F80",
                padding: 32,
              }}
            >
              <img
                src={PostImage}
                alt="Content Creator"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  filter: "grayscale(100%)",
                }}
              />
            </Box>
            <div style={{ color: "#A3A3A3", fontSize: 20 }}>
              No {notFoundText} yet
            </div>
            <Button
              component={Link}
              to="/?visionType=Explore"
              variant="contained"
              className={classes.sendButton}
              style={{
                textTransform: "none",
                fontSize: "16px",
                padding: "10px 32px",
              }}
            >
              Discover content
            </Button>
          </Box>
        )}
        {posts.map((post, idx) => {
          const visibleComments = expandedPosts[post._id]
            ? commentsByPost[post._id] || []
            : (commentsByPost[post._id] || []).slice(0, 1);

          const isOwner =
            me?.id === post.userId ||
            me?._id === post.userId ||
            me?.id === post.user._id ||
            me?._id === post.user._id;
          const isPremiumPost = post.subscriptionType === "premium";

          const hasPremiumSubForCreator =
            post.creatorSubscriptionTier === "premium";

          const shouldShowUpgradeOverlay =
            !isOwner && isPremiumPost && !hasPremiumSubForCreator;

          return (
            <Card
              key={post._id}
              className={`${classes.root} MyFeed`}
              ref={
                idx === posts.length - 1
                  ? lastPostRef
                  : idx === 0
                    ? firstPostRef
                    : null
              }
            >
              {/* Categories Display */}
              {post.categories && post.categories.length > 0 && (
                <Box
                  style={{
                    padding: "12px 16px",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "8px",
                    borderBottom: "1px solid #363636",
                  }}
                >
                  {post.categories.map(category => (
                    <Box
                      key={category._id}
                      style={{
                        backgroundColor: "#363636",
                        color: "#fff",
                        padding: "4px 12px",
                        borderRadius: "16px",
                        fontSize: "12px",
                        fontWeight: 500,
                      }}
                    >
                      {category.name}
                    </Box>
                  ))}
                </Box>
              )}
              {
                <div className={classes.cardContainer}>
                  <CardHeader
                    avatar={
                      <Avatar
                        onClick={() =>
                          handleProfileClick(
                            post.user.username || post.user._id
                          )
                        }
                        src={
                          loadingMedia[post.user.profileImg]
                            ? undefined
                            : mediaUrls[post.user.profileImg] ||
                              placeholderImage
                        }
                        className={classes.avatar}
                      >
                        {loadingMedia[post.user.profileImg] && (
                          <CircularProgress size={24} />
                        )}
                      </Avatar>
                    }
                    title={
                      <div
                        className={classes.nameHelp}
                        onClick={() =>
                          handleProfileClick(
                            post.user.username || post.user._id
                          )
                        }
                      >
                        <Typography
                          className={classes.firstLastName}
                          variant="body1"
                        >
                          {post.user.firstName} {post.user.lastName}
                          <VerifiedShieldHelpIcon
                            style={{ marginLeft: "5px" }}
                            className={classes.verifiedIcon}
                          />
                        </Typography>
                        <Typography
                          className={classes.usersName}
                          variant="body1"
                          data-guide-step={
                            idx === 0 ? "first-post-username" : undefined
                          }
                          ref={
                            idx === 0
                              ? (firstPostUsernameRef as React.RefObject<HTMLDivElement>)
                              : undefined
                          }
                        >
                          @{post.user.username}
                        </Typography>
                      </div>
                    }
                    action={
                      <EllipsisMenuButton
                        post={post}
                        onTip={handleTipFromMenu}
                      />
                    }
                    className={classes.header}
                  />
                  <div
                    className={classes.mediaContainer}
                    onTouchStart={e => handleTouchStart(e, post._id)}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                    onMouseDown={e => handleMouseDown(e, post._id)}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                  >
                    <Slider
                      className={classes.sliderStyle}
                      {...sliderSettings}
                      infinite={post.content.length > 1}
                      arrows={post.content.length > 1 && !isMobile}
                      prevArrow={
                        post.content.length > 1 && !isMobile ? (
                          <PrevArrow />
                        ) : undefined
                      }
                      nextArrow={
                        post.content.length > 1 && !isMobile ? (
                          <NextArrow />
                        ) : undefined
                      }
                    >
                      {post.content.map((c, i) => (
                        <div key={i} className={likeAnimation.slideWrapper}>
                          <div className={likeAnimation.likeOverlay}>
                            {showBigLikeIndex === post._id && (
                              <Favorite className={likeAnimation.bigHeart} />
                            )}
                          </div>
                          <div style={{ position: "relative" }}>
                            {!post.access &&
                              me?.id !== post.userId &&
                              me?._id !== post.userId &&
                              me?.id !== post.user._id &&
                              me?._id !== post.user._id && (
                                <div
                                  style={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    width: "100%",
                                    height: "100%",
                                    zIndex: 1,
                                  }}
                                />
                              )}
                            {c.type === "video" ? (
                              loadingMedia[c.mediaUrl] ? (
                                <div
                                  className={`${classes.media_img} ${"MyFeed"}`}
                                  style={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    backgroundColor: "#1a1a1a",
                                  }}
                                >
                                  <CircularProgress size={40} />
                                </div>
                              ) : c.isBlurred ? (
                                // Для відео з isBlurred бекенд вже надає заблюрене прев'ю через mediaUrl, показуємо як зображення
                                <div
                                  className={
                                    isLikesOrBookmarks
                                      ? classes.mediaContainerLikesBookmarks
                                      : ""
                                  }
                                  style={{
                                    position: "relative",
                                    width: "100%",
                                    cursor: "pointer",
                                  }}
                                  onClick={e => handlePostClick(post, e)}
                                  onDoubleClick={() =>
                                    bigLikeHandler(post._id, idx)
                                  }
                                >
                                  <img
                                    src={
                                      mediaUrls[c.mediaUrl] ||
                                      c.mediaUrl ||
                                      placeholderImage
                                    }
                                    alt={`slide-${i}`}
                                    className={`${classes.media_img} ${"MyFeed"} ${
                                      isLikesOrBookmarks ? "likesBookmarks" : ""
                                    }`}
                                    style={{ display: "block" }}
                                  />
                                </div>
                              ) : (
                                <div
                                  className={
                                    isLikesOrBookmarks
                                      ? classes.mediaContainerLikesBookmarks
                                      : ""
                                  }
                                  style={{
                                    position: "relative",
                                    width: "100%",
                                    cursor: "pointer",
                                  }}
                                  onClick={e => handlePostClick(post, e)}
                                  onDoubleClick={() =>
                                    bigLikeHandler(post._id, idx)
                                  }
                                >
                                  <video
                                    src={
                                      mediaUrls[c.mediaUrl] ||
                                      c.mediaUrl ||
                                      placeholderImage
                                    }
                                    controls
                                    controlsList="nodownload noremoteplayback noplaybackrate"
                                    disablePictureInPicture
                                    autoPlay={false}
                                    muted
                                    loop
                                    className={`${classes.media_video} ${"MyFeed"} ${
                                      isLikesOrBookmarks ? "likesBookmarks" : ""
                                    }`}
                                    style={{ display: "block" }}
                                  />
                                </div>
                              )
                            ) : loadingMedia[c.mediaUrl] ? (
                              <div
                                className={`${classes.media_img} ${"MyFeed"}`}
                                style={{
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  backgroundColor: "#1a1a1a",
                                }}
                              >
                                <CircularProgress size={40} />
                              </div>
                            ) : (
                              <div
                                className={
                                  isLikesOrBookmarks
                                    ? classes.mediaContainerLikesBookmarks
                                    : ""
                                }
                                style={{
                                  position: "relative",
                                  width: "100%",
                                  cursor: "pointer",
                                }}
                                onClick={e => handlePostClick(post, e)}
                                onDoubleClick={() =>
                                  bigLikeHandler(post._id, idx)
                                }
                              >
                                <img
                                  src={
                                    mediaUrls[c.mediaUrl] || placeholderImage
                                  }
                                  alt={`slide-${i}`}
                                  className={`${classes.media_img} ${"MyFeed"} ${isLikesOrBookmarks ? "likesBookmarks" : ""}`}
                                  style={{ display: "block" }}
                                />
                                <ChevronsLeftRight
                                  size={24}
                                  color="#FFFFFF"
                                  style={{
                                    position: "absolute",
                                    bottom: 20,
                                    right: 20,
                                    transform: "rotate(135deg) scale(1.4)",
                                    zIndex: 3,
                                    pointerEvents: "none",
                                    opacity: 0.95,
                                    strokeWidth: 1,
                                    backgroundColor: "#FFFFFF33",
                                    borderRadius: "50%",
                                  }}
                                />
                              </div>
                            )}
                            {shouldShowUpgradeOverlay && (
                              <Box
                                style={{
                                  position: "absolute",
                                  inset: 0,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  padding: 12,
                                  textAlign: "center",
                                  background:
                                    "linear-gradient(180deg, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.9) 100%)",
                                  zIndex: 4,
                                }}
                              >
                                <Box
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    gap: 8,
                                  }}
                                >
                                  <img
                                    src={subscribeIcon}
                                    alt="Upgrade subscription"
                                    style={{ width: 32, height: 26 }}
                                  />
                                  <Typography
                                    style={{
                                      color: "#FFFFFF",
                                      fontSize: 14,
                                      fontWeight: 500,
                                    }}
                                  >
                                    Upgrade your Subscription
                                    <br />
                                    to unlock more exclusive content
                                  </Typography>
                                </Box>
                              </Box>
                            )}
                            {!post.access &&
                              me?.id !== post.userId &&
                              me?._id !== post.userId &&
                              me?.id !== post.user._id &&
                              me?._id !== post.user._id && (
                                <Button
                                  style={{
                                    position: "absolute",
                                    bottom: "50%",
                                    left: "50%",
                                    transform: "translate(-50%, 50%)",
                                    zIndex: 2,
                                    padding: "10px 20px",
                                    color: "#FE5A97",
                                    border: "1px solid #FE5A97",
                                    borderRadius: "5px",
                                    fontStyle: "normal",
                                    fontWeight: 800,
                                    fontSize: "14px",
                                    textTransform: "none",
                                  }}
                                  onClick={(
                                    e: React.MouseEvent<HTMLButtonElement>
                                  ) => {
                                    e.stopPropagation();
                                    handlePurchasePost(post);
                                  }}
                                >
                                  ${post.price} Purchase{" "}
                                  {c.type === "video" ? "Video" : "Image"}
                                </Button>
                              )}
                          </div>
                        </div>
                      ))}
                    </Slider>
                  </div>

                  {/* username + description + tags overlay (My Feed) */}
                  <Box className={`${classes.description} feedOverlay`}>
                    <Typography
                      variant="body2"
                      style={{
                        color: "#FF78AA",
                        fontWeight: 600,
                        fontSize: isMobile ? 14 : 20, // 14px на мобільних
                        marginBottom: 4,
                      }}
                    >
                      @{post.user.username}
                    </Typography>

                    {post.description && (
                      <Typography
                        variant="body2"
                        style={{
                          color: "#FFFFFF",
                          fontSize: isMobile ? 12 : 20, // 12px на мобільних
                          fontWeight: 600,
                          marginBottom:
                            Array.isArray(post.hashtags) &&
                            post.hashtags.length > 0
                              ? 4
                              : 0,
                        }}
                      >
                        {post.description}
                      </Typography>
                    )}

                    {Array.isArray(post.hashtags) &&
                      post.hashtags.length > 0 && (
                        <Box display="flex" flexWrap="wrap">
                          {post.hashtags.map((tag, index) => (
                            <Typography
                              key={index}
                              variant="body2"
                              style={{
                                fontSize: isMobile ? 12 : 20, // 12px на мобільних
                                fontWeight: 400,
                                color: "#FFFFFF",
                                marginRight: 6,
                              }}
                            >
                              #{tag}
                            </Typography>
                          ))}
                        </Box>
                      )}
                    <Typography
                      variant="body2"
                      style={{
                        color: "#FFFFFFCC",
                        fontSize: isMobile ? 12 : 20,
                        fontWeight: 400,
                        marginTop: "8px",
                        textAlign: "left",
                      }}
                    >
                      {formatPostDate(post.createdAt)}
                    </Typography>
                  </Box>

                  <Box className={classes.statsContainer}>
                    {/* left side: likes & comments */}
                    <Box
                      display="flex"
                      alignItems="center"
                      style={{ gap: 16, cursor: "pointer" }}
                    >
                      <Box className={classes.stat}>
                        <Tooltip title="Like" arrow placement="top">
                          <IconButton
                            className={classes.stats}
                            onClick={() => {
                              setIsAnimating(false);
                              likeClick(post._id, idx);
                              setTimeout(() => setIsAnimating(true), 0);
                              setTimeout(() => setIsAnimating(false), 500);
                            }}
                          >
                            <HeartPostIcon isLiked={post.isLiked} />
                          </IconButton>
                        </Tooltip>
                        <Typography
                          variant="body2"
                          style={{ color: "#FFFFFF", fontSize: 16 }}
                        >
                          {post.likesCount}
                        </Typography>
                      </Box>

                      <Box className={classes.stat}>
                        <Tooltip title="Comments" arrow placement="top">
                          <IconButton
                            className={classes.stats}
                            onClick={e => {
                              e.stopPropagation();
                              if (
                                !post.access &&
                                me?.id !== post.userId &&
                                me?._id !== post.userId &&
                                me?.id !== post.user._id &&
                                me?._id !== post.user._id &&
                                isFollowing[post.user._id] !==
                                  FollowStatus.FOLLOWING
                              ) {
                                showToast(
                                  "Unlock this post to comment",
                                  "info"
                                );
                                return;
                              }
                              openPostDialog(post, { showComments: true });
                            }}
                          >
                            <CommentPostIcon />
                            <Typography
                              variant="body2"
                              style={{
                                color: "#FFFFFF",
                                fontSize: 16,
                                marginLeft: 14,
                              }}
                            >
                              {post.commentsCount || 0}
                            </Typography>
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>

                    <Box flexGrow={1} />

                    {/* right side: tip, bookmark, share */}
                    <Box
                      display="flex"
                      alignItems="center"
                      style={{ gap: isMobile ? 8 : 12 }}
                    >
                      <Tooltip title="Tip Creator" arrow placement="top">
                        <IconButton
                          onClick={() => {
                            setSelectedUserId(post.user._id);
                            setTipDialogVisible(true);
                          }}
                          style={{
                            color: "#FFFFFF",
                            backgroundColor: "#1F1F1F80",
                          }}
                        >
                          <DollarSign />
                        </IconButton>
                      </Tooltip>

                      <Tooltip
                        title={
                          post.isBookmarked ? "Remove bookmark" : "Bookmark"
                        }
                        arrow
                        placement="top"
                      >
                        <IconButton
                          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                            e.stopPropagation();
                            handleToggleBookmark(post);
                          }}
                          style={{
                            color: post.isBookmarked ? "#FE5A97" : "#fff",
                            backgroundColor: "#1F1F1F80",
                          }}
                        >
                          <Bookmark
                            size={24}
                            color={post.isBookmarked ? "#FE5A97" : "#fff"}
                            fill={post.isBookmarked ? "#FE5A97" : "none"}
                          />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Share" arrow placement="top">
                        <IconButton
                          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                            e.stopPropagation();
                            handleShare(post);
                          }}
                          style={{
                            color: "#fff",
                            paddingRight: isMobile ? "10px" : "15px",
                            backgroundColor: "#1F1F1F80",
                          }}
                        >
                          <SharePostIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>

                  {shareData && (
                    <ShareLinkDialog
                      open={shareModalOpen}
                      onClose={() => setShareModalOpen(false)}
                      shareData={shareData}
                    />
                  )}

                  {/* Username, description and hashtags are now rendered as an overlay on the image for feed posts */}
                  {openCommentsByPost[post._id] &&
                    (post.access ? (
                      <Box className={classes.commentSection}>
                        <div
                          className={classes.commentInput}
                          style={{ position: "relative" }}
                        >
                          <TextField
                            fullWidth
                            variant="standard"
                            placeholder="Add a comment..."
                            value={commentTexts[post._id] || ""}
                            onChange={e =>
                              setCommentTexts(prev => ({
                                ...prev,
                                [post._id]: e.target.value,
                              }))
                            }
                            onKeyDown={(
                              e: React.KeyboardEvent<HTMLDivElement>
                            ) => {
                              if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                handleSendComment(post._id, idx);
                              }
                            }}
                            InputProps={{ disableUnderline: true }}
                            inputRef={(el: HTMLInputElement | null) => {
                              if (el) {
                                commentInputRefs.current[post._id] = el;
                              } else {
                                delete commentInputRefs.current[post._id];
                              }
                            }}
                          />

                          <IconButton
                            onClick={() =>
                              setEmojiForPost(cur =>
                                cur === post._id ? null : post._id
                              )
                            }
                          >
                            <SentimentSatisfiedAlt
                              style={{ color: "#FE5A97" }}
                            />
                          </IconButton>

                          {emojiOpenFor === post._id && (
                            <ClickAwayListener
                              onClickAway={() => setEmojiForPost(null)}
                            >
                              <Box
                                style={{
                                  position: "absolute",
                                  bottom: 78,
                                  right: 0,
                                  zIndex: 1000,
                                }}
                              >
                                <Picker
                                  data={data}
                                  onEmojiSelect={handleEmojiSelectFor(post._id)}
                                  theme="dark"
                                  searchPosition="sticky"
                                />
                              </Box>
                            </ClickAwayListener>
                          )}
                        </div>
                      </Box>
                    ) : (
                      <Typography
                        style={{ padding: "0 16px 12px", opacity: 0.7 }}
                      >
                        Unlock to comment
                      </Typography>
                    ))}

                  <TipDialog
                    open={tipDialogVisible && !!selectedUserId}
                    onClose={() => {
                      setTipDialogVisible(false);
                      setSelectedUserId(null);
                    }}
                    suggestedTips={
                      selectedUserId
                        ? profileDataMap[selectedUserId]?.suggestedTips
                        : undefined
                    }
                    minimumTipAmount={
                      selectedUserId
                        ? profileDataMap[selectedUserId]?.minimumTipAmount
                        : undefined
                    }
                    username={
                      selectedUserId
                        ? posts.find(p => p.user._id === selectedUserId)?.user
                            .username
                        : undefined
                    }
                    tipForUserId={selectedUserId || undefined}
                    userId={me?.id}
                    email={me?.email}
                    onPaymentSuccess={handleTipSuccess}
                  />
                  {!me ? (
                    ""
                  ) : (
                    <CommentsModal
                      open={true}
                      postId={post._id}
                      postUserId={post.userId}
                      currentUserId={me._id}
                      postOwnerId={post.user._id}
                      comments={visibleComments || []}
                      onCommentsChange={(updatedComments: Comment[]) =>
                        handleCommentsChange(updatedComments, post._id)
                      }
                      onClose={() => {}}
                      onFetchComments={async () => {
                        const loadedCount =
                          commentsByPost[post._id]?.length || 5;
                        await fetchCommentsForPost(post, 1, false, loadedCount);
                      }}
                      mediaUrls={mediaUrls}
                      loadingMedia={loadingMedia}
                      onLoadReplies={(commentId: string) =>
                        loadRepliesForComment(post, commentId)
                      }
                      RenderAvatar={RenderAvatar}
                      isMobile={isMobile}
                      classes={classesForComments}
                      variant="mobile"
                      currentUserProfileImg={me?.profileImg}
                      postOwnerUsername={post.user.username}
                      postOwnerFullName={`${post.user.firstName} ${post.user.lastName}`}
                      postOwnerProfileImg={post.user.profileImg}
                      autoScrollOnOpen={initialShowComments}
                      isFeedPost={true}
                      hasMoreByPost={
                        !expandedPosts[post._id]
                          ? hasMoreByPost[post._id] ||
                            (commentsByPost[post._id]?.length || 0) > 1
                          : hasMoreByPost[post._id]
                      }
                      handleLoadMoreComments={() => {
                        setExpandedPosts(prev => ({
                          ...prev,
                          [post._id]: true,
                        }));
                        if (
                          (commentsByPost[post._id]?.length || 0) <
                          (post.commentsCount || 0)
                        ) {
                          loadMoreComments(post);
                        }
                      }}
                      onCommentAdded={() => {
                        setPosts(prev =>
                          prev.map(p =>
                            p._id === post._id
                              ? {
                                  ...p,
                                  commentsCount: (p.commentsCount || 0) + 1,
                                }
                              : p
                          )
                        );
                      }}
                    />
                  )}
                </div>
              }
            </Card>
          );
        })}

        {selectedPostForPurchase && purchasePostDialogVisible && (
          <UnlockPaidContentModal
            open={purchasePostDialogVisible}
            onClose={() => {
              setPurchasePostDialogVisible(false);
              setSelectedPostForPurchase(null);
            }}
            previewUrl={
              mediaUrls[selectedPostForPurchase.content[0]?.mediaUrl] ||
              videoIconSvg
            }
            isVideo={selectedPostForPurchase.content[0]?.type === "video"}
            price={selectedPostForPurchase.price}
            userId={me?.id || (me as any)?._id || ""}
            email={me?.email || ""}
            postId={selectedPostForPurchase._id}
            tipForUserId={selectedPostForPurchase.userId}
            onProceed={handleCompletePurchase}
            onPaymentSuccess={handlePurchaseSuccess}
          />
        )}

        {isLoading && posts.length > 0 && (
          <div
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              marginBottom: "2rem",
            }}
          >
            <CircularProgress />
          </div>
        )}

        {selectedPost && (
          <PostDialog
            open={dialogOpen}
            post={selectedPost}
            onClose={handleCloseDialog}
            visionType={visionType}
            scrollPosition={
              (location.state as any)?.scrollPosition ||
              Number(sessionStorage.getItem("myFeedScrollPosition")) ||
              0
            }
            onPostDeleted={handlePostDeleted}
            onEditPost={handleEditPost}
            initialShowComments={initialShowComments}
          />
        )}

        <Modal
          open={editPostDialogVisible}
          onClose={() => {
            setEditPostDialogVisible(false);
            setSelectedPostForEdit(null);
            setRawHashtagInput("");
          }}
          className={classesForComments.modalSubscriptionWrapper}
        >
          <Box className={classesForComments.modalTipWrapper}>
            <DialogTitle className={classesForComments.modalTitle}>
              Edit Post
            </DialogTitle>
            <DialogContent className={classesForComments.modalContent}>
              <Typography
                className={classesForComments.modalSubtitle}
                style={{ marginBottom: "16px" }}
              >
                Update your post details
              </Typography>

              <TextField
                label="Description"
                multiline
                rows={4}
                fullWidth
                value={editDescription}
                onChange={e => setEditDescription(e.target.value)}
                variant="outlined"
                inputProps={{ maxLength: 2000 }}
                placeholder="Enter post description..."
                helperText={`${editDescription.length}/2000 characters`}
                style={{ marginBottom: "16px" }}
                className={classesForComments.editPostTextField}
              />

              <Box style={{ marginBottom: "16px", width: "100%" }}>
                <Typography
                  style={{ color: "#fff", marginBottom: 8, fontWeight: 500 }}
                >
                  Hashtags
                </Typography>

                {editHashtags.length > 0 && (
                  <Box
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "8px",
                      marginBottom: "8px",
                    }}
                  >
                    {editHashtags.map((tag, index) => (
                      <Box
                        key={index}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          backgroundColor: "#FE5A97",
                          color: "#fff",
                          padding: "4px 8px",
                          borderRadius: "16px",
                          fontSize: "12px",
                          fontWeight: 500,
                        }}
                      >
                        #{tag}
                        <Button
                          onClick={() => removeEditHashtag(tag)}
                          style={{
                            minWidth: "16px",
                            width: "16px",
                            height: "16px",
                            marginLeft: "4px",
                            color: "#fff",
                            fontSize: "12px",
                            padding: "0",
                            lineHeight: "1",
                          }}
                        >
                          ×
                        </Button>
                      </Box>
                    ))}
                  </Box>
                )}

                <input
                  type="text"
                  value={rawHashtagInput}
                  placeholder="#exclusive, #behindthescenes, #luxury"
                  onChange={handleEditHashtagInputChange}
                  onKeyPress={handleEditHashtagKeyPress}
                  disabled={editHashtags.length >= 5}
                  style={{
                    width: "100%",
                    padding: "12px",
                    borderRadius: "8px",
                    border: "1px solid #363636",
                    background:
                      editHashtags.length >= 5 ? "#2a2a2a" : "#1e1e1e",
                    color: editHashtags.length >= 5 ? "#666" : "#fff",
                    fontSize: "14px",
                    opacity: editHashtags.length >= 5 ? 0.6 : 1,
                  }}
                />
                <Typography
                  style={{
                    color: "#999",
                    fontSize: "12px",
                    marginTop: "4px",
                    fontStyle: "italic",
                  }}
                >
                  {editHashtags.length >= 5
                    ? "Maximum 5 hashtags reached"
                    : "Add up to 5 hashtags to reach more fans. Press Enter to add"}
                </Typography>
              </Box>

              {isPaidPostType(selectedPostForEdit?.type) && (
                <TextField
                  label="Price"
                  type="text"
                  fullWidth
                  value={editPriceInput}
                  onChange={e => {
                    const parsedValue = parsePostPriceInput(e.target.value);
                    setEditPrice(parsedValue);
                    setEditPriceInput(formatPostPriceInput(parsedValue));
                  }}
                  variant="outlined"
                  inputProps={{
                    inputMode: "numeric",
                    "aria-label": "Price in dollars and cents",
                  }}
                  placeholder="$0.00"
                  helperText="Minimum price: $5.00 (for private posts)"
                  className={classesForComments.editPostTextField}
                />
              )}
            </DialogContent>
            <DialogActions className={classesForComments.modalActions}>
              <Button
                onClick={() => {
                  setEditPostDialogVisible(false);
                  setSelectedPostForEdit(null);
                  setRawHashtagInput("");
                }}
                className={classesForComments.cancelButton}
                disabled={isSavingPost}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSavePostEdit}
                className={classesForComments.sendButton}
                disabled={isSavingPost}
              >
                {isSavingPost ? (
                  <>
                    <CircularProgress size={16} style={{ marginRight: 8 }} />
                    Saving...
                  </>
                ) : (
                  "Save Changes"
                )}
              </Button>
            </DialogActions>
          </Box>
        </Modal>

        {/* Bottom Sheet for Comments on Mobile */}
        <Drawer
          anchor="bottom"
          open={commentsBottomSheetOpen}
          onClose={() => {
            setCommentsBottomSheetOpen(false);
            setSelectedPostForComments(null);
            setCommentText("");
          }}
          PaperProps={{
            style: {
              backgroundColor: "#1F1F1F",
              borderTopLeftRadius: 20,
              borderTopRightRadius: 20,
              maxHeight: "90vh",
              display: "flex",
              flexDirection: "column",
            },
          }}
        >
          <Box
            style={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
              maxHeight: "90vh",
            }}
          >
            {/* Header */}
            <Box
              style={{
                padding: "16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography
                style={{
                  color: "#FFFFFF",
                  fontSize: 18,
                  fontWeight: 600,
                }}
              >
                Comments
              </Typography>
              <IconButton
                onClick={() => {
                  setCommentsBottomSheetOpen(false);
                  setSelectedPostForComments(null);
                  setCommentText("");
                }}
                style={{ color: "#FFFFFF" }}
              >
                <Close />
              </IconButton>
            </Box>

            {/* Comments List */}
            <Box
              style={{
                flex: 1,
                overflowY: "auto",
                overflowX: "hidden",
                padding: "16px",
                paddingBottom: "80px", // Space for input
              }}
            >
              {loadingByPost ? (
                <Box
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    padding: "20px",
                  }}
                >
                  <CircularProgress size={24} style={{ color: "#FE5A97" }} />
                </Box>
              ) : (commentsByPost[selectedPostForComments?._id ?? ""] ?? [])
                  .length === 0 ? (
                <Typography
                  style={{
                    color: "#FFFFFF80",
                    textAlign: "center",
                    padding: "20px",
                  }}
                >
                  No comments yet
                </Typography>
              ) : (
                (commentsByPost[selectedPostForComments?._id ?? ""] ?? []).map(
                  (c, idx) => (
                    <React.Fragment key={c.id}>
                      <Box
                        style={{
                          display: "flex",
                          marginBottom: "16px",
                          gap: "12px",
                        }}
                      >
                        <RenderAvatar
                          profileImg={c.user.profileImg}
                          style={{
                            width: 32,
                            height: 32,
                            cursor: "pointer",
                            flexShrink: 0,
                          }}
                          onClick={() => {
                            const scrollPosition = window.scrollY;
                            sessionStorage.setItem(
                              "myFeedScrollPosition",
                              scrollPosition.toString()
                            );
                            history.push({
                              pathname: `/${c.user.username}`,
                              state: {
                                fromPostDialog: true,
                                postId: selectedPostForComments?._id,
                                userId: selectedPostForComments?.userId,
                                previousPath:
                                  location.pathname + location.search,
                              },
                            });
                            setCommentsBottomSheetOpen(false);
                          }}
                        />
                        <Box style={{ flex: 1 }}>
                          <Typography
                            style={{
                              color: "#FFFFFF",
                              fontSize: 14,
                              fontWeight: 600,
                              cursor: "pointer",
                              marginBottom: "4px",
                            }}
                            onClick={() => {
                              const scrollPosition = window.scrollY;
                              sessionStorage.setItem(
                                "myFeedScrollPosition",
                                scrollPosition.toString()
                              );
                              history.push({
                                pathname: `/${c.user.username}`,
                                state: {
                                  fromPostDialog: true,
                                  postId: selectedPostForComments?._id,
                                  userId: selectedPostForComments?.userId,
                                  previousPath:
                                    location.pathname + location.search,
                                },
                              });
                              setCommentsBottomSheetOpen(false);
                            }}
                          >
                            {c.user.username}
                          </Typography>
                          <Typography
                            style={{ color: "#FFFFFF", fontSize: 14 }}
                          >
                            <CommentText
                              text={c.text}
                              onMentionClick={(username: string) => {
                                const scrollPosition = window.scrollY;
                                sessionStorage.setItem(
                                  "myFeedScrollPosition",
                                  scrollPosition.toString()
                                );
                                history.push({
                                  pathname: `/${username}`,
                                  state: {
                                    fromPostDialog: true,
                                    postId: selectedPostForComments?._id,
                                    userId: selectedPostForComments?.userId,
                                    previousPath:
                                      location.pathname + location.search,
                                  },
                                });
                                setCommentsBottomSheetOpen(false);
                              }}
                              className=""
                            />
                          </Typography>
                          <Box
                            style={{
                              display: "flex",
                              gap: "8px",
                              marginTop: "4px",
                              alignItems: "center",
                            }}
                          >
                            <Typography
                              variant="caption"
                              style={{
                                color: "#FFFFFF",
                                fontSize: 12,
                              }}
                            >
                              {formatCommentTime(c.createdAt)}
                            </Typography>
                          </Box>
                        </Box>
                        <Box
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            alignSelf: "flex-start",
                            marginTop: "4px",
                          }}
                        >
                          <IconButton
                            size="small"
                            onClick={async () => {
                              if (!selectedPostForComments) return;
                              try {
                                await likeUserPostCommentAPI(
                                  selectedPostForComments._id,
                                  c.id
                                );
                                setCommentsByPost(prev => {
                                  const postId = selectedPostForComments._id;
                                  const postComments = [
                                    ...(prev[postId] || []),
                                  ];
                                  const comment = postComments.find(
                                    com => com.id === c.id
                                  );
                                  if (comment.id === c.id) {
                                    comment.isLiked = !comment.isLiked;
                                    comment.likesCount += comment.isLiked
                                      ? 1
                                      : -1;
                                  }
                                  return {
                                    ...prev,
                                    [postId]: postComments,
                                  };
                                });
                              } catch (err) {
                                showToast("Error liking comment", "error");
                              }
                            }}
                            style={{
                              padding: "4px",
                              color: c.isLiked ? "#FE5A97" : "#FFFFFF",
                            }}
                          >
                            <Favorite
                              style={{
                                fontSize: 16,
                                fill: c.isLiked ? "#FE5A97" : "currentColor",
                                color: c.isLiked ? "#FE5A97" : "#FFFFFF",
                              }}
                            />
                          </IconButton>
                          <Typography
                            variant="caption"
                            style={{
                              color: "#FFFFFF",
                              fontSize: 12,
                              margin: 0,
                            }}
                          >
                            {c.likesCount}
                          </Typography>
                        </Box>
                      </Box>
                      {c.repliesCount > 0 && c.replies.length > 0 && (
                        <Box style={{ marginLeft: "44px", marginTop: "8px" }}>
                          {showReplies[c.id] ? (
                            <div>
                              <Box
                                onClick={() =>
                                  toggleReplies(
                                    c.id,
                                    (c.repliesCountAllWithNested ??
                                      c.repliesCount ??
                                      c.replies?.length ??
                                      0) as number
                                  )
                                }
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  marginBottom: "8px",
                                  cursor: "pointer",
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  style={{
                                    fontWeight: 700,
                                    color: "#FFFFFFCC",
                                    fontSize: "12px",
                                  }}
                                >
                                  Hide replies
                                </Typography>
                                <ChevronUp
                                  size={18}
                                  style={{ color: "#FFFFFFCC" }}
                                />
                              </Box>
                              <div
                                style={{
                                  marginLeft: "8px",
                                }}
                              >
                                {c.replies.map((reply: any) => (
                                  <Box
                                    key={reply.id}
                                    style={{
                                      display: "flex",
                                      marginBottom: "12px",
                                      gap: "8px",
                                    }}
                                  >
                                    <RenderAvatar
                                      profileImg={reply.user.profileImg}
                                      style={{
                                        width: 24,
                                        height: 24,
                                        cursor: "pointer",
                                        flexShrink: 0,
                                      }}
                                      onClick={() => {
                                        const scrollPosition = window.scrollY;
                                        sessionStorage.setItem(
                                          "myFeedScrollPosition",
                                          scrollPosition.toString()
                                        );
                                        history.push({
                                          pathname: `/${reply.user.username}`,
                                          state: {
                                            fromPostDialog: true,
                                            postId:
                                              selectedPostForComments?._id,
                                            userId:
                                              selectedPostForComments?.userId,
                                            previousPath:
                                              location.pathname +
                                              location.search,
                                          },
                                        });
                                        setCommentsBottomSheetOpen(false);
                                      }}
                                    />
                                    <Box style={{ flex: 1 }}>
                                      <Typography
                                        style={{
                                          color: "#FFFFFF",
                                          fontSize: 12,
                                          fontWeight: 600,
                                          cursor: "pointer",
                                          marginBottom: "4px",
                                        }}
                                        onClick={() => {
                                          const scrollPosition = window.scrollY;
                                          sessionStorage.setItem(
                                            "myFeedScrollPosition",
                                            scrollPosition.toString()
                                          );
                                          history.push({
                                            pathname: `/${reply.user.username}`,
                                            state: {
                                              fromPostDialog: true,
                                              postId:
                                                selectedPostForComments?._id,
                                              userId:
                                                selectedPostForComments?.userId,
                                              previousPath:
                                                location.pathname +
                                                location.search,
                                            },
                                          });
                                          setCommentsBottomSheetOpen(false);
                                        }}
                                      >
                                        {reply.user.username}
                                      </Typography>
                                      <Typography
                                        style={{
                                          color: "#FFFFFF",
                                          fontSize: 12,
                                        }}
                                      >
                                        <CommentText
                                          text={reply.text}
                                          onMentionClick={(
                                            username: string
                                          ) => {
                                            const scrollPosition =
                                              window.scrollY;
                                            sessionStorage.setItem(
                                              "myFeedScrollPosition",
                                              scrollPosition.toString()
                                            );
                                            history.push({
                                              pathname: `/${username}`,
                                              state: {
                                                fromPostDialog: true,
                                                postId:
                                                  selectedPostForComments?._id,
                                                userId:
                                                  selectedPostForComments?.userId,
                                                previousPath:
                                                  location.pathname +
                                                  location.search,
                                              },
                                            });
                                            setCommentsBottomSheetOpen(false);
                                          }}
                                          className=""
                                        />
                                      </Typography>
                                      <Box
                                        style={{
                                          display: "flex",
                                          gap: "8px",
                                          marginTop: "4px",
                                          alignItems: "center",
                                        }}
                                      >
                                        <Typography
                                          variant="caption"
                                          style={{
                                            color: "#FFFFFF",
                                            fontSize: 10,
                                          }}
                                        >
                                          {formatCommentTime(reply.createdAt)}
                                        </Typography>
                                      </Box>
                                    </Box>
                                    <Box
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "4px",
                                        alignSelf: "flex-start",
                                        marginTop: "4px",
                                      }}
                                    >
                                      <IconButton
                                        size="small"
                                        onClick={async () => {
                                          if (!selectedPostForComments) return;
                                          try {
                                            await likeUserPostCommentAPI(
                                              selectedPostForComments._id,
                                              reply.id
                                            );
                                            setCommentsByPost(prev => {
                                              const postId =
                                                selectedPostForComments._id;
                                              const postComments = [
                                                ...(prev[postId] || []),
                                              ];
                                              const comment = postComments.find(
                                                com => com.id === c.id
                                              );
                                              if (comment && comment.replies) {
                                                const replyIdx =
                                                  comment.replies.findIndex(
                                                    (r: any) =>
                                                      r.id === reply.id
                                                  );
                                                if (replyIdx !== -1) {
                                                  comment.replies[
                                                    replyIdx
                                                  ].isLiked =
                                                    !comment.replies[replyIdx]
                                                      .isLiked;
                                                  comment.replies[
                                                    replyIdx
                                                  ].likesCount += comment
                                                    .replies[replyIdx].isLiked
                                                    ? 1
                                                    : -1;
                                                }
                                              }
                                              if (comment && comment.replies) {
                                                const replyIdx =
                                                  comment.replies.findIndex(
                                                    (r: any) =>
                                                      r.id === reply.id
                                                  );
                                                if (replyIdx !== -1) {
                                                  comment.replies[
                                                    replyIdx
                                                  ].isLiked =
                                                    !comment.replies[replyIdx]
                                                      .isLiked;
                                                  comment.replies[
                                                    replyIdx
                                                  ].likesCount += comment
                                                    .replies[replyIdx].isLiked
                                                    ? 1
                                                    : -1;
                                                }
                                              }
                                              return {
                                                ...prev,
                                                [postId]: postComments,
                                              };
                                            });
                                          } catch (err) {
                                            showToast(
                                              "Error liking comment",
                                              "error"
                                            );
                                          }
                                        }}
                                        style={{
                                          padding: "2px",
                                          color: reply.isLiked
                                            ? "#FE5A97"
                                            : "#FFFFFF",
                                        }}
                                      >
                                        <Favorite
                                          style={{
                                            fontSize: 14,
                                            fill: reply.isLiked
                                              ? "#FE5A97"
                                              : "currentColor",
                                            color: reply.isLiked
                                              ? "#FE5A97"
                                              : "#FFFFFF",
                                          }}
                                        />
                                      </IconButton>
                                      <Typography
                                        variant="caption"
                                        style={{
                                          color: "#FFFFFF",
                                          fontSize: 10,
                                          margin: 0,
                                        }}
                                      >
                                        {reply.likesCount}
                                      </Typography>
                                    </Box>
                                  </Box>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <Box
                              onClick={() =>
                                toggleReplies(
                                  c.id,
                                  (c.repliesCountAllWithNested ??
                                    c.repliesCount ??
                                    c.replies?.length ??
                                    0) as number
                                )
                              }
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                cursor: "pointer",
                              }}
                            >
                              <Typography
                                variant="caption"
                                style={{
                                  fontWeight: 700,
                                  color: "#FFFFFFCC",
                                  fontSize: "12px",
                                }}
                              >
                                {typeof c.repliesCountAllWithNested === "number"
                                  ? `View ${c.repliesCountAllWithNested} ${
                                      c.repliesCountAllWithNested === 1
                                        ? "reply"
                                        : "replies"
                                    }`
                                  : "View replies"}
                              </Typography>
                              <ChevronDown
                                size={18}
                                style={{ color: "#FFFFFFCC" }}
                              />
                            </Box>
                          )}
                        </Box>
                      )}
                    </React.Fragment>
                  )
                )
              )}
            </Box>

            {/* Input Section - Fixed at Bottom */}
            <Box
              style={{
                flexShrink: 0,
                backgroundColor: "#1F1F1F",
                padding: "12px 16px",
                paddingBottom: "max(12px, env(safe-area-inset-bottom))",
              }}
            >
              <Box
                style={{
                  position: "relative",
                  width: "100%",
                  backgroundColor: "#131313",
                  borderRadius: "12px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <InputBase
                  placeholder="Add a comment..."
                  value={commentText}
                  onChange={e => setCommentText(e.target.value)}
                  inputRef={commentInputRef}
                  onKeyDown={e => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSendCommentForBottomSheet();
                    }
                  }}
                  style={{
                    flex: 1,
                    color: "#FFFFFF",
                    padding: "8px 100px 8px 8px",
                  }}
                  inputProps={{
                    style: {
                      color: "#FFFFFF",
                    },
                  }}
                />
                <IconButton
                  onClick={() =>
                    setShowEmojiPickerMobile(!showEmojiPickerMobile)
                  }
                  style={{
                    position: "absolute",
                    right: "70px",
                    padding: "4px",
                    color: "#FFFFFF",
                  }}
                >
                  <SentimentSatisfiedAlt
                    style={{ fontSize: "20px", color: "#FFFFFF" }}
                  />
                </IconButton>
                {showEmojiPickerMobile && (
                  <ClickAwayListener
                    onClickAway={() => setShowEmojiPickerMobile(false)}
                  >
                    <Box
                      style={{
                        position: "absolute",
                        bottom: "48px",
                        right: "8px",
                        zIndex: 1000,
                      }}
                    >
                      <Picker
                        data={data}
                        onEmojiSelect={handleEmojiSelectMobile}
                        theme="dark"
                        searchPosition="sticky"
                      />
                    </Box>
                  </ClickAwayListener>
                )}
                <IconButton
                  disabled={!commentText.trim()}
                  onClick={handleSendCommentForBottomSheet}
                  style={{
                    position: "absolute",
                    right: "4px",
                    padding: "14px 18px",
                    backgroundColor: "#FE5A97",
                    borderRadius: "6px",
                    color: "#FFFFFF",
                  }}
                >
                  <Send size={20} />
                </IconButton>
              </Box>
            </Box>
          </Box>
        </Drawer>
      </Box>
    );
  }
);

CreatorPost.displayName = "CreatorPost";

export default CreatorPost;
